import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { paymentAPI, handleAPIError } from '../services/api';

const Payment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const plans = [
  
    {
      id: 'monthly',
      title: 'Monthly Care Plan',
      price: '$79',
      period: '/month',
      description: 'Most cost-effective, continuous care.',
      features: [
        'One ADHD nurse session per billing cycle',
        'Weekly SMS check-ins',
        'Monthly AI analysis and report',
        'Video or phone nurse consultation',
        'Personalised insights dashboard'
      ],
      priceId: 'price_1S2mZbPNYCu1z4Ed45Nt6bKo',
      isBestValue: true
    },
    {
      id: 'weekly',
      title: 'Weekly Care Plan',
      price: '$20',
      period: '/week',
      description: 'Short-term, flexible support.',
      features: [
        'One ADHD nurse session per billing cycle',
        'Weekly SMS check-ins',
        'Monthly AI analysis and report',
        'Video or phone nurse consultation',
        'Personalised insights dashboard'
      ],
      priceId: 'price_1S2mZ5PNYCu1z4Ed740hZR2s',
      isBestValue: false
    }
  ];

  const handlePlanSelection = async (planId) => {
    try {
      setLoading(planId);
      setError('');

      const plan = plans.find(p => p.id === planId);
      const paymentResult = await paymentAPI.createCheckoutSession({
        priceId: plan.priceId,
        successUrl: `${process.env.FRONTEND_URL || 'https://adhdrapidcare.com'}/payment-success?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/payment/cancel`
      });

      window.location.href = paymentResult.url;
    } catch (err) {
      setError(handleAPIError(err, 'Payment initialization failed. Please try again.'));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-8">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Choose Your ADHD Care Plan
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Select a subscription that fits your needs and start personalised  care today.
          </p>
        </div>

        {/* Plan Card */}
        <div className="grid md:grid-cols-1 gap-8 mb-8">
          <div className="relative bg-white rounded-xl border-2 p-8 shadow-lg">
            {/* Title */}
            <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
              Your ADHD Care Plan
            </h3>

            {/* Shared Description */}
            <p className="text-gray-600 mb-1 text-center">
              Select weekly or monthly payment and start your personalised  care today
            </p>

            <p className="text-gray-600 text-center mb-3">
              All plans include access to nurse sessions, SMS tracking, AI analysis and personalised  support
            </p>

            {/* Features */}
            <ul className="space-y-3 mb-8">
              {plans[0].features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <div className="w-5 h-5 bg-blue-600 rounded-full mr-3 mt-0.5 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>

            {/* Plan Selection */}
            <div className="space-y-6">
              {/* Plan Buttons */}
              <div className="grid sm:grid-cols-2 gap-6">
                {plans.map((plan) => (
                  <div key={plan.id} className="space-y-2">
                    <button
                      onClick={() => handlePlanSelection(plan.id)}
                      disabled={loading === plan.id}
                      className={`relative w-full py-4 px-6 rounded-lg font-semibold text-white transition-all duration-200
                        ${loading === plan.id 
                          ? plan.id === 'monthly' 
                            ? 'bg-blue-400 cursor-not-allowed'
                            : 'bg-indigo-400 cursor-not-allowed'
                          : plan.id === 'monthly'
                            ? 'bg-blue-600 hover:bg-blue-700 transition-colors duration-150'
                            : 'bg-indigo-600 hover:bg-indigo-700 transition-colors duration-150'
                        }
                      `}
                    >
                      {plan.isBestValue && (
                        <span className={`absolute -top-3 left-1/2 transform -translate-x-1/2 text-xs px-3 py-1 rounded-full bg-white border ${loading === plan.id ? 'text-blue-400 border-blue-400' : 'text-blue-600 border-blue-600'}`}>
                          BEST VALUE
                        </span>
                      )}
                      {loading === plan.id ? 'Processing...' : `Pay Now ${plan.price}${plan.period}`}
                    </button>
                    <p className="text-sm text-gray-600 text-center">
                      Your card will be charged {plan.price} every {plan.id === 'monthly' ? 'month' : 'week'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-center">
            {error}
          </div>
        )}

        {/* Footer Note */}
        <div className="text-center">
          <p className="text-lg text-gray-600 bg-white/70 backdrop-blur-sm rounded-lg py-3 px-6 inline-block">
            You can cancel or change your plan at any time from your patient portal.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Payment;